/**
 * MessageInput - Minimalist mission control styled input
 *
 * Features:
 * - Clean monospace design
 * - Enter key submission
 * - Retro terminal aesthetic
 */

import { FormEvent } from 'react'

interface MessageInputProps {
  onSendMessage: (content: string) => void
  disabled: boolean
  placeholder?: string
  disabledPlaceholder?: string
}

function MessageInput({
  onSendMessage,
  disabled,
  placeholder = 'Enter query...',
  disabledPlaceholder = 'Connecting...',
}: MessageInputProps) {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const input = e.currentTarget.elements.namedItem('message') as HTMLInputElement
    if (input.value.trim() && !disabled) {
      onSendMessage(input.value.trim())
      input.value = ''
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-3">
      {/* Terminal prompt indicator */}
      <span className="text-retro-amber font-mono text-sm select-none">▸</span>

      <input
        type="text"
        name="message"
        data-testid="message-input"
        placeholder={disabled ? disabledPlaceholder : placeholder}
        disabled={disabled}
        autoComplete="off"
        className="
          flex-1 bg-transparent border-none outline-none
          font-mono text-sm text-dark-700 placeholder-retro-dim
          disabled:opacity-40 disabled:cursor-not-allowed
        "
      />

      <button
        type="submit"
        data-testid="send-button"
        disabled={disabled}
        className="
          text-xs font-mono uppercase tracking-wider px-2 py-1
          text-retro-cyan hover:text-retro-amber
          disabled:opacity-40 disabled:cursor-not-allowed
          transition-colors
        "
      >
        Send
      </button>
    </form>
  )
}

export default MessageInput
